import React from 'react';
import request from '../../../utils/request';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import Moment from 'react-moment';
import loaderGif from '../../common/no_bg_loader.gif';
import FileImage from '../../common/document.jpg';

type ClaimsProps = {
  rfqDetails: any;
  offerStatus?: any;
  onDataLoad?: (attachments: any[]) => void;
};

function Claims({ rfqDetails, offerStatus = null, onDataLoad }: ClaimsProps) {
  const [fetchloader, setFetchLoader] = React.useState(true);
  const [claimsDatas, setClaimsDatas] = React.useState([]);
  const [claimAttachmentData, setClaimAttachmentData] = React.useState([]);

  // track API completion
  const [claimsLoaded, setClaimsLoaded] = React.useState(false);
  const [attachmentsLoaded, setAttachmentsLoaded] = React.useState(false);

  React.useEffect(() => {
    if (rfqDetails.id) {
      setFetchLoader(true);
      setClaimsLoaded(false);
      setAttachmentsLoaded(false);

      fetchClaimsDetails();
      fetchClaimAttachmentDetails();
    }
  }, [rfqDetails]);

  // Call parent only when BOTH APIs completed
  React.useEffect(() => {
    if (claimsLoaded && attachmentsLoaded) {
      setFetchLoader(false);
      if (!onDataLoad) return;

      const hasClaims = claimsDatas.length > 0;
      const hasAttachments = claimAttachmentData.length > 0;

      // If only attachments exist → send fake data to parent
      if (!hasClaims && hasAttachments) {
        onDataLoad([{}]);
      } else {
        onDataLoad(claimsDatas);
      }

      
    }
  }, [claimsLoaded, attachmentsLoaded]);

  const fetchClaimsDetails = () => {
    request({
      url: 'claims/getClaimsList',
      method: 'post',
      data: { rfqId: rfqDetails.id }
    }).then(
      (response) => {
        const list = response['claimsData'] || [];
        setClaimsDatas(list);
        setClaimsLoaded(true);
      },
      () => {
        setClaimsDatas([]);
        setClaimsLoaded(true);
      }
    );
  };

  const fetchClaimAttachmentDetails = () => {
    request({
      url: 'rfq/getClaimAttachments',
      method: 'post',
      data: { rfqId: rfqDetails.id }
    }).then(
      (response) => {
        const att = response['data'] || [];
        setClaimAttachmentData(att);
        setAttachmentsLoaded(true);
        
      },
      () => {
        setClaimAttachmentData([]);
        setAttachmentsLoaded(true);
      }
    );
  };

  // -------------------- UI RENDER --------------------
  if (fetchloader) {
    return (
      <Stack
        sx={{ color: 'grey.500' }}
        spacing={2}
        direction="row"
        justifyContent="center"
        mt={5}
      >
        <img src={loaderGif} width={100} height={100} alt="loader" />
      </Stack>
    );
  }

  return (
    <>
      {/* CLAIM TABLE */}
      {claimsDatas.length > 0 &&
        (rfqDetails?.claimMode === 'Enter claim data' ||
          rfqDetails?.claimMode === 'Claim data with attachment') && (
          <div>
            <Typography
              gutterBottom
              variant="h4"
              className="blockHeader heading"
              component="div"
              style={{
                background: 'rgb(9 84 174)',
                color: '#fff',
                padding: '16px',
                paddingTop: '2px',
                paddingBottom: '2px',
                marginBottom: '3vh'
              }}
            >
              <div style={{ paddingTop: '0.3%', paddingBottom: '0.3%' }}>
                Claim Details
              </div>
            </Typography>

            <Table className="SectionHeading" size="small">
              <TableRow sx={{ background: 'rgba(187, 220, 214, 0.32)' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>DATE OF CLAIM</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>CLAIM AMOUNT</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>CLAIM STATUS</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>SETTLED AMOUNT</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>DATE OF LOSS</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>CAUSE OF LOSS</TableCell>
              </TableRow>

              <TableBody>
                {claimsDatas.map((claims, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {claims.claimDate ? (
                        <Moment format="DD-MM-yyyy">{claims.claimDate}</Moment>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>

                    <TableCell>{claims.claimAmount}</TableCell>

                    <TableCell>{claims.claimStatus}</TableCell>

                    <TableCell>
                      {claims.settledAmount ? claims.settledAmount : 'N/A'}
                    </TableCell>

                    <TableCell>
                      {claims.dateOfLoss ? (
                        <Moment format="DD-MM-yyyy">{claims.dateOfLoss}</Moment>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>

                    <TableCell>
                      {claims.causeOfLoss && claims.causeOfLoss.length > 24 ? (
                        <Tooltip title={claims.causeOfLoss} arrow>
                          <span>{claims.causeOfLoss.substring(0, 24) + '...'}</span>
                        </Tooltip>
                      ) : (
                        claims.causeOfLoss || 'N/A'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      {/* ATTACHMENTS */}
      {claimAttachmentData.length > 0 &&
        (rfqDetails?.claimMode === 'Claim data with attachment' ||
          rfqDetails?.claimMode === 'Upload claim data') && (
          <div>
            <Typography
              gutterBottom
              variant="h4"
              className="blockHeader heading"
              component="div"
              style={{
                background: 'rgb(9 84 174)',
                color: '#fff',
                padding: '16px',
                paddingTop: '2px',
                paddingBottom: '2px'
              }}
            >
              <div style={{ paddingTop: '0.3%', paddingBottom: '0.3%' }}>
                Claim Details with attachment
              </div>
            </Typography>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                marginTop: '16px'
              }}
            >
              {claimAttachmentData.map((attachment, index) => (
                <div
                  key={index}
                  style={{ textAlign: 'left', cursor: 'pointer' }}
                  onClick={() =>
                    window.open(attachment.url, '_blank', 'noopener,noreferrer')
                  }
                >
                  <img src={FileImage} width={100} height={100} alt="file" />
                  <Typography variant="body2" style={{ marginTop: '8px' }}>
                    {attachment.fileName
                      ? attachment.fileName.substring(0, 10) +
                        (attachment.fileName.length > 10 ? '...' : '')
                      : `Document ${index + 1}`}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        )}
    </>
  );
}

export default Claims;
